import { useEffect, useRef } from "react";
import { TICKS_PER_SECOND, idx, unitStats, type UnitKind } from "@empire/core-sim";
import { BoardView } from "../../pixi/BoardView.js";
import { SPEEDS, secondsAt, useReplayStore, verdictWord } from "../../store/replay.js";
import { inspectTower, inspectUnit } from "../../pixi/fx.js";
import { TOWER_GLYPH, unitKindsFromPlan } from "../theme/glyphs.js";
import { UnitMark } from "../components/UnitMark.js";

/**
 * Watching a battle back (S4-1, S4-3, S4-4).
 *
 * Full screen over everything else, because a replay is the one thing in this
 * app you look at rather than operate. The controls are the four from S18:
 * play/pause, speed, scrub, and skip to the end.
 */
export function ReplayView(): JSX.Element | null {
  const battleId = useReplayStore((s) => s.battleId);
  const status = useReplayStore((s) => s.status);

  if (!battleId) return null;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-bg">
      <Header />
      {status === "ready" ? <Board /> : <Message />}
      {status === "ready" ? <Controls /> : null}
    </div>
  );
}

function Header(): JSX.Element {
  const replay = useReplayStore((s) => s.replay);
  const verified = useReplayStore((s) => s.verified);
  const close = useReplayStore((s) => s.close);
  const role = useReplayStore((s) => s.role);

  return (
    <header className="empire-header flex shrink-0 items-center gap-3 border-b border-panel-edge bg-bg-raised px-3 py-1">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-ink">
          {replay ? verdictWord(replay.result.outcome === "breach", role) : "Battle"}
        </p>
        {replay ? (
          <p className="text-[11px] tabular-nums text-ink-dim">
            {replay.result.army.reached}/{replay.result.army.sent} reached ·{" "}
            {replay.result.army.lost} lost · {(replay.result.ticks / TICKS_PER_SECOND).toFixed(1)}s
            {verified ? (
              // Worth saying out loud: this is not a video of a battle, it is
              // the battle, re-run here and checked against the server's own
              // fingerprint.
              <span className="ml-2 text-ok" data-testid="verified">
                · verified
              </span>
            ) : null}
          </p>
        ) : null}
      </div>
      <button
        type="button"
        data-testid="replay-close"
        onClick={close}
        className="h-11 shrink-0 rounded-lg border border-panel-edge bg-panel px-3 text-xs font-semibold text-ink-dim hover:text-ink"
      >
        Close
      </button>
    </header>
  );
}

/** Loading, a version mismatch, or the one message that matters: a desync. */
function Message(): JSX.Element {
  const status = useReplayStore((s) => s.status);
  const error = useReplayStore((s) => s.error);

  return (
    <div className="flex min-h-0 flex-1 items-center justify-center p-6">
      {status === "loading" ? (
        <p className="text-sm text-ink-dim">Loading the battle…</p>
      ) : (
        <div
          className={`max-w-sm rounded-xl border px-4 py-3 ${
            status === "desync"
              ? "border-danger/40 bg-danger/10 text-danger"
              : "border-warn/30 bg-warn/10 text-warn"
          }`}
          data-testid={status === "desync" ? "desync" : "replay-error"}
        >
          <p className="text-sm font-semibold">
            {status === "desync" ? "This replay does not add up" : "Can't play this back"}
          </p>
          <p className="mt-1 text-[11px] opacity-90">{error}</p>
        </div>
      )}
    </div>
  );
}

function Board(): JSX.Element {
  const hostRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<BoardView | null>(null);
  const layout = useReplayStore((s) => s.layout);
  const fog = useReplayStore((s) => s.fog);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    let cancelled = false;
    let view: BoardView | undefined;

    void BoardView.mount(host, useReplayStore.getState().layout).then((mounted) => {
      if (cancelled) {
        mounted.destroy();
        return;
      }
      view = mounted;
      viewRef.current = mounted;
      mounted.setFog(useReplayStore.getState().fog);
      // Watching, not building: zoom to where the battle happens.
      mounted.frameAction();
      // The plan IS the id -> kind mapping (units are numbered in plan order),
      // so the dots can be told apart without widening the wire format.
      const plan = useReplayStore.getState().replay?.plan;
      mounted.setUnitKinds(plan ? unitKindsFromPlan(plan) : []);

      // Tap while paused to ask what something is and what it has done (S4-5).
      mounted.on({
        onTap: (at, unitId) => {
          const { playing, layout } = useReplayStore.getState();
          if (playing) return;
          const tile = at ? idx(at.x, at.y, layout.size) : null;
          const tower = tile !== null && layout.tiles[tile]?.tower ? tile : null;
          useReplayStore.getState().inspect(unitId, unitId === null ? tower : null);
        },
      });

      mounted.app.ticker.add((ticker) => {
        // Playback is driven by the render loop, so speed is tied to real time
        // rather than to how often React happens to re-render.
        useReplayStore.getState().advance(ticker.deltaMS);
        const { frames, position, inspecting } = useReplayStore.getState();
        mounted.setInspecting(inspecting?.unitId ?? null, inspecting?.tile ?? null);
        mounted.setReplay(frames, position, useReplayStore.getState().playing);
      });
    });

    return () => {
      cancelled = true;
      viewRef.current = null;
      view?.destroy();
    };
  }, []);

  useEffect(() => {
    // The layout never changes mid-replay, but the fog does when a different
    // battle is opened without unmounting.
    void layout;
    void fog;
  }, [layout, fog]);

  return (
    <div className="relative min-h-0 flex-1 overflow-hidden">
      <div
        ref={hostRef}
        className="h-full w-full touch-none [&>canvas]:block"
        data-testid="replay-board"
      />
      <Unknown />
      <div className="absolute bottom-10 right-2 flex flex-col gap-1" aria-label="Battle camera">
        <button
          type="button"
          className="empire-control"
          aria-label="Zoom in"
          onClick={() => viewRef.current?.zoomBy(1.2)}
        >
          +
        </button>
        <button
          type="button"
          className="empire-control px-2 text-[11px]"
          aria-label="Fit battle"
          onClick={() => viewRef.current?.frameAction()}
        >
          Fit
        </button>
        <button
          type="button"
          className="empire-control"
          aria-label="Zoom out"
          onClick={() => viewRef.current?.zoomBy(1 / 1.2)}
        >
          −
        </button>
      </div>
      <Inspector />
      <InspectHint />
    </div>
  );
}

/** How much of this battle you were never allowed to see (S4-3). */
function Unknown(): JSX.Element | null {
  const replay = useReplayStore((s) => s.replay);
  if (!replay || replay.view.unknownCount === 0) return null;

  return (
    <div
      className="pointer-events-none absolute left-3 top-3 rounded bg-black/50 px-2 py-1 text-[11px] text-ink-dim"
      data-testid="replay-unknown"
    >
      {replay.view.unknownCount} tiles hidden — your units never got there
    </div>
  );
}

/** Said once, quietly, because nobody taps a board they were not invited to. */
function InspectHint(): JSX.Element | null {
  const playing = useReplayStore((s) => s.playing);
  const inspecting = useReplayStore((s) => s.inspecting);
  if (playing || inspecting) return null;

  return (
    <div
      className="pointer-events-none absolute inset-x-0 bottom-3 text-center text-[11px] text-ink-dim opacity-60"
      data-testid="inspect-hint"
    >
      Tap a unit or a tower to see what it has done
    </div>
  );
}

/**
 * What that thing is, and what it has done by now (S4-5).
 *
 * Deliberately "by now" rather than "in total". The reason to pause a replay is
 * that something has just gone wrong and you want to know what was doing the
 * damage at that moment — a whole-battle total would answer a question nobody
 * stopped to ask.
 *
 * A fogged attacker's popover is fogged for free: it reads the same effect
 * stream the animation does, and a tower they never earned is not in it. So
 * their hits show as damage taken from nothing, which is exactly what they
 * know.
 */
function Inspector(): JSX.Element | null {
  const inspecting = useReplayStore((s) => s.inspecting);
  const frames = useReplayStore((s) => s.frames);
  const position = useReplayStore((s) => s.position);
  const layout = useReplayStore((s) => s.layout);
  const replay = useReplayStore((s) => s.replay);
  const dismiss = useReplayStore((s) => s.inspect);

  if (!inspecting || frames.length === 0) return null;

  const kinds = replay ? unitKindsFromPlan(replay.plan) : [];
  let title: JSX.Element;
  let rows: [string, string][];

  if (inspecting.unitId !== null) {
    const report = inspectUnit(frames, position, inspecting.unitId);
    const kind = kinds[inspecting.unitId] as UnitKind | undefined;
    title = (
      <span className="flex items-center gap-2">
        {kind ? <UnitMark kind={kind} size={13} /> : null}
        {kind ? unitStats(kind).name : `Unit ${inspecting.unitId}`}
      </span>
    );
    rows = [
      ["Damage taken", String(report.damageTaken)],
      ["Times hit", String(report.timesHit)],
      [
        "State",
        report.status === "dead" ? "Killed" : report.status === "reached" ? "Got in" : "Walking",
      ],
    ];
    if (report.slowed) rows.push(["Frost", "Slowed"]);
  } else if (inspecting.tile !== null) {
    const tower = layout.tiles[inspecting.tile]?.tower;
    const report = inspectTower(frames, position, inspecting.tile);
    title = (
      <span className="flex items-center gap-2">
        <span className="grid h-[13px] w-[13px] place-items-center rounded-sm bg-gold text-[9px] font-bold text-bg">
          {tower ? TOWER_GLYPH[tower.kind] : "?"}
        </span>
        {tower ? `${tower.kind} tower` : "Tower"}
        {tower && tower.level > 1 ? ` · level ${tower.level}` : ""}
      </span>
    );
    rows = [
      ["Shots fired", String(report.shots)],
      ["Damage dealt", String(report.damageDealt)],
      ["Kills", String(report.kills)],
    ];
  } else {
    return null;
  }

  return (
    <div
      className="absolute bottom-3 left-3 right-3 rounded-xl border border-panel-edge bg-panel/95 px-3 py-2.5 shadow-lg"
      data-testid="inspector"
    >
      <div className="flex items-center gap-2">
        <p className="min-w-0 flex-1 truncate text-xs font-semibold capitalize text-ink">{title}</p>
        <button
          type="button"
          data-testid="inspector-close"
          onClick={() => dismiss(null, null)}
          className="-my-1 h-9 shrink-0 rounded-lg px-2 text-[11px] font-semibold text-ink-dim hover:text-ink"
        >
          Close
        </button>
      </div>
      <dl className="mt-1.5 flex flex-wrap gap-x-5 gap-y-1">
        {rows.map(([label, value]) => (
          <div key={label} className="flex items-baseline gap-1.5">
            <dt className="text-[10px] uppercase tracking-wide text-ink-dim opacity-70">{label}</dt>
            <dd className="text-xs font-semibold tabular-nums text-ink">{value}</dd>
          </div>
        ))}
      </dl>
      <p className="mt-1 text-[10px] text-ink-dim opacity-60">
        so far, at {secondsAt(frames, position).toFixed(1)}s
      </p>
    </div>
  );
}

function Controls(): JSX.Element {
  const frames = useReplayStore((s) => s.frames);
  const position = useReplayStore((s) => s.position);
  const playing = useReplayStore((s) => s.playing);
  const speed = useReplayStore((s) => s.speed);
  const toggle = useReplayStore((s) => s.togglePlay);
  const setSpeed = useReplayStore((s) => s.setSpeed);
  const seek = useReplayStore((s) => s.seek);
  const skip = useReplayStore((s) => s.skipToEnd);

  const last = Math.max(frames.length - 1, 0);
  const atEnd = position >= last;

  return (
    <div
      className="shrink-0 border-t border-panel-edge bg-bg-raised px-4 py-3"
      style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}
    >
      <input
        type="range"
        aria-label="Scrub"
        data-testid="scrub"
        min={0}
        max={last}
        step={1}
        value={Math.floor(position)}
        onChange={(e) => seek(Number(e.target.value))}
        className="w-full accent-gold"
      />
      <div className="mt-2 flex items-center gap-2">
        <button
          type="button"
          data-testid="play"
          onClick={toggle}
          className="h-11 rounded-lg bg-gold px-4 text-xs font-bold text-bg"
        >
          {playing ? "Pause" : atEnd ? "Replay" : "Play"}
        </button>

        <span className="w-16 text-[11px] tabular-nums text-ink-dim" data-testid="clock">
          {secondsAt(frames, position).toFixed(1)}s
        </span>

        <div className="ml-auto flex items-center gap-1">
          {SPEEDS.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setSpeed(s)}
              className={`h-11 rounded-lg px-3 text-xs font-semibold ${
                speed === s ? "bg-gold text-bg" : "bg-panel text-ink-dim hover:text-ink"
              }`}
            >
              {s}×
            </button>
          ))}
          <button
            type="button"
            data-testid="skip"
            onClick={skip}
            className="h-11 rounded-lg bg-panel px-3 text-xs font-semibold text-ink-dim hover:text-ink"
          >
            End
          </button>
        </div>
      </div>
    </div>
  );
}
